
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';

export interface Area    { id:number; storeId:number; description:string; }
export interface Table   { id:number; storeId:number; areaId:number; tableName:string; seatingCapacity:number; }
export interface AreaDTO { storeId:number; name:string }
export interface TableDTO{ storeId:number; areaId:number; tableName:string; seatingCapacity:number; tableDetailsJson:string }

export type TableShape = 'ROUND' | 'RECTANGLE' | 'SQUARE' | 'BOOTH' | 'OVAL';

/**
 * Copies one store's sections + tables onto sibling stores of the same
 * business. replaceExisting=false merges (reuses same-named sections,
 * skips same-named tables); true wipes the target layout first.
 */
export interface ReplicateLayoutRequest {
  sourceStoreId: number;
  targetStoreIds: number[];
  replaceExisting: boolean;
}

/** Per-target outcome — a failed target is reported, not thrown. */
export interface ReplicateLayoutResult {
  targetStoreId: number;
  targetStoreName: string | null;
  success: boolean;
  message: string;
  areasCreated: number;
  areasReused: number;
  areasDeleted: number;
  tablesCreated: number;
  tablesSkipped: number;
  tablesDeleted: number;
  skippedTables: string[];
  relocatedTables: string[];
}

@Injectable({ providedIn:'root' })
export class TableService {
  private base = environment.NEW_BACKEND_URL + 'api/tables';
  constructor(private http: HttpClient){}

  getAreas(storeId:number) {
    return this.http.get<Area[]>(`${this.base}/areas`, { params: new HttpParams().set('storeId',storeId) });
  }
  addArea(dto:AreaDTO) {
    return this.http.post<Area>(`${this.base}/areas`, dto);
  }
  getTables(storeId:number) {
    return this.http.get<Table[]>(this.base, { params: new HttpParams().set('storeId',storeId) });
  }
  addTable(dto:TableDTO) {
    return this.http.post<Table>(this.base, dto);
  }
  deleteTable(id:number) {
    return this.http.delete(`${this.base}/${id}/delete`);
  }
  updateTable(id: number, body: any) {
    return this.http.patch<Table>(`${this.base}/${id}`, body);
  }
  
  moveTable(id: number, body: { row: number; col: number }) {
    return this.http.patch<Table>(`${this.base}/${id}/position`, body);
  }
  
  updateDetails(id: number, body: { shape?: TableShape; color?: string; row?: number; col?: number }) {
    return this.http.patch<Table>(`${this.base}/${id}/details`, body);
  }
  
  deleteArea(id: number, force = false) {
    return this.http.delete(`${this.base}/areas/${id}`, { params: new HttpParams().set('force', String(force)) });
  }

  /**
   * Returns the table's QR token, generating one if it doesn't exist
   * yet. Idempotent — the backend hands back the existing token for
   * every subsequent call so vendors can reprint QR codes without
   * invalidating what customers have already scanned.
   */
  generateQrToken(tableId: number) {
    return this.http.post<{ token: string }>(`${this.base}/${tableId}/qr-token`, {});
  }

  /**
   * Replicates the source store's whole layout onto every target store
   * in one call. Always resolves with one result per target — check
   * `success` on each entry rather than relying on the HTTP status.
   */
  replicateLayout(body: ReplicateLayoutRequest) {
    return this.http.post<ReplicateLayoutResult[]>(`${this.base}/replicate`, body);
  }
}
